import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '../../../shared/services/api.service';
import { DateService } from '../../../shared/services/date.service';
import { ColDef } from 'ag-grid-community';

export interface OrderData {
  id: number;
  code: string;
  package_id: string;
  currency: string;
  quantity: number;
  type: string;
  description: string | null;
  esim_type: string;
  validity: number;
  package: string;
  data: string;
  price: number;
  pricing_model: string;
  text: string | null;
  voice: string | null;
  net_price: number;
  created_at: string;
  manual_installation: string;
  qrcode_installation: string;
  installation_guides: {
    en: string;
  };
  customer: {
    _id: string;
    emailId: string;
    name: string;
    mobile?: string;
    [key: string]: any; // Additional customer properties
  };
}

export interface OrdersResponse {
  error: boolean;
  data: {
    data: OrderData[];
    links: any;
    meta: any;
  };
  message: string;
}

@Component({
  standalone: false,
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.scss'
})
export class OrdersComponent implements OnInit {
  gridApi: any;
  gridColumnApi: any;
  apiUrl: any;
  loading = true;
  error: string | null = null;
  orders: OrderData[] = [];

  constructor(
    private apiService: ApiService,
    private router: Router,
    private _dateService: DateService
  ) {}

  columnDefs: ColDef[] = [
    {
      headerName: 'ID',
      field: 'id',
      editable: false,
      filter: true,
      sortable: true,
      width: 100
    },
    {
      headerName: 'Order Code',
      field: 'code',
      editable: false,
      filter: true,
      sortable: true,
      width: 150
    },
    {
      headerName: 'Package',
      field: 'package',
      editable: false,
      filter: true,
      sortable: true,
      width: 200
    },
    {
      headerName: 'Package ID',
      field: 'package_id',
      editable: false,
      filter: true,
      sortable: true,
      width: 150
    },
    {
      headerName: 'Type',
      field: 'type',
      editable: false,
      filter: true,
      sortable: true,
      width: 100
    },
    {
      headerName: 'ESIM Type',
      field: 'esim_type',
      editable: false,
      filter: true,
      sortable: true,
      width: 120
    },
    {
      headerName: 'Data',
      field: 'data',
      editable: false,
      filter: true,
      sortable: true,
      width: 100
    },
    {
      headerName: 'Validity (Days)',
      field: 'validity',
      editable: false,
      filter: true,
      sortable: true,
      width: 140
    },
    {
      headerName: 'Price',
      field: 'price',
      editable: false,
      filter: true,
      sortable: true,
      width: 100,
      valueFormatter: (params) => `${params.data.currency} ${params.value}`
    },
  
    {
      headerName: 'Quantity',
      field: 'quantity',
      editable: false,
      filter: true,
      sortable: true,
      width: 100
    },
    {
      headerName: 'Created At',
      field: 'created_at',
      editable: false,
      filter: true,
      sortable: true,
      width: 180,
      valueFormatter: (params) => this._dateService.getFormattedDateWithAMPM(params.value)
    },
    {
      headerName: 'Customer Name',
      field: 'customer.name',
      editable: false,
      filter: true,
      sortable: true,
      width: 150
    },
    {
      headerName: 'Customer Email',
      field: 'customer.emailId',
      editable: false,
      filter: true,
      sortable: true,
      width: 200
    },
    {
      headerName: 'Actions',
      field: 'actions',
      editable: false,
      filter: false,
      sortable: false,
      width: 120,
      cellRenderer: (params: any) => {
        const button = document.createElement('button');
        button.classList.add('btn', 'btn-sm', 'btn-primary');
        button.innerHTML = '<i class="bi bi-eye-fill"></i>';
        button.addEventListener('click', () => {
          this.viewDetails(params.data);
        });
        return button;
      }
    }
  ];

  gridOptions: any = {
    paginationPageSize: 15,
    cacheBlockSize: 15,
    pagination: true,
    rowModelType: 'clientSide', // Changed from 'infinite' to 'clientSide'
    paginationPageSizeSelector: [15, 20, 50, 100],
    rowData: this.orders,
    rowSelection: 'single',
    context: {
      componentParent: this,
    },
    defaultColDef: {
      resizable: true,
      filter: true,
      sortable: true
    },
    onGridReady: (params: any) => {
      params.api.sizeColumnsToFit();
      window.addEventListener('resize', function() {
        setTimeout(function() {
          params.api.sizeColumnsToFit();
        });
      });
    }
  };

  ngOnInit(): void {
    this.loadOrders();
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    
    params.api.sizeColumnsToFit();
    window.addEventListener('resize', function() {
      setTimeout(function() {
        params.api.sizeColumnsToFit();
      });
    });
  }

  loadOrders(): void {
    // The JWT interceptor will automatically add the token header
    this.apiService.getData('/api/airalo/orders').subscribe({
      next: (response: OrdersResponse) => {
        if (!response.error && response.data && response.data.data) {
          this.orders = response.data.data;
          if (this.gridApi) {
            this.gridApi.setRowData(this.orders);
          } else {
            this.gridOptions.rowData = this.orders;
          }
        } else {
          this.error = 'Failed to load orders data';
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching orders:', err);
        this.error = 'Error loading orders';
        this.loading = false;
      }
    });
  }

  viewDetails(order: OrderData): void {
    this.router.navigate(['/app/orders', order.id]);
  }
}